"use client";

// SSR-safe: initial values are optimistic (true) to match server render.
// Real detection runs in useEffect (client-only) to avoid hydration mismatch.
// isScreenCaptureSupported handles the Safari hard-block (returns false on Safari).

import { useState, useEffect } from "react";
import { isScreenCaptureSupported } from "@/features/recorder/utils/screen-capture-support";

export interface BrowserRecordingSupport {
  /** true = getUserMedia exists AND (MediaRecorder exists OR polyfill active) */
  isAudioSupported: boolean;
  /** true = getUserMedia exists AND video isTypeSupported returns truthy */
  isVideoSupported: boolean;
  /** true = getDisplayMedia present AND not hard-blocked on Safari */
  isScreenSupported: boolean;
  isAnySupported: boolean;
  unsupportedKey: string | null;
  isDetected: boolean;
}

const OPTIMISTIC_INITIAL: BrowserRecordingSupport = {
  isAudioSupported: true,
  isVideoSupported: true,
  isScreenSupported: true,
  isAnySupported: true,
  unsupportedKey: null,
  isDetected: false,
};

export function useBrowserRecordingSupport(
  options?: { isPolyfillActive?: boolean },
): BrowserRecordingSupport {
  const isPolyfillActive = options?.isPolyfillActive ?? false; // pessimistic default; caller sets true when polyfill loads

  const [support, setSupport] = useState<BrowserRecordingSupport>(OPTIMISTIC_INITIAL);

  useEffect(() => {
    const hasGetUserMedia =
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia;

    const hasMediaRecorder =
      typeof MediaRecorder !== "undefined" || isPolyfillActive;

    const isAudioSupported = hasGetUserMedia && hasMediaRecorder;

    const isVideoSupported =
      hasGetUserMedia &&
      typeof MediaRecorder !== "undefined" &&
      (MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus") ||
        MediaRecorder.isTypeSupported("video/webm") ||
        MediaRecorder.isTypeSupported("video/mp4"));

    const isScreenSupported = isScreenCaptureSupported().supported;

    const isAnySupported = isAudioSupported || isVideoSupported || isScreenSupported;

    const unsupportedKey = !isAudioSupported
      ? "ERROR_NOTIFICATION.LIVE_RECORD.WEBCAM"
      : null;

    setSupport({
      isAudioSupported,
      isVideoSupported,
      isScreenSupported,
      isAnySupported,
      unsupportedKey,
      isDetected: true,
    });
  }, [isPolyfillActive]);

  return support;
}
